import type { LucideProps } from 'lucide-react';

// Define LucideIcon type for component references
type LucideIcon = React.ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>>;

/**
 * Navigation Link Structure
 * Represents a single navigation link with label and destination
 */
export interface NavigationLink {
  label: string;
  href?: string; // For anchor links (e.g., "/#features")
  path?: string; // For router links (e.g., "/login")
}

/**
 * Enhanced Navigation Link with Icon
 * Used for app navigation with visual icons
 */
export interface NavigationLinkWithIcon extends NavigationLink {
  icon: LucideIcon;
  section?: 'main' | 'learning' | 'development' | 'progress' | 'enterprise' | 'tenant';
}

/**
 * General Navigation Link with Icon
 * Used for general header navigation with visual icons
 */
export interface GeneralNavLinkWithIcon extends NavigationLink {
  icon: LucideIcon;
}

/**
 * Social Media Link Structure
 * Represents external social media links
 */
export interface SocialLink {
  icon: LucideIcon;
  href: string;
  label: string;
}

/**
 * Footer Links Structure
 * Organized footer links by categories
 */
export interface FooterLinks {
  product: NavigationLink[];
  company: NavigationLink[];
  resources: NavigationLink[];
  legal: NavigationLink[];
}

/**
 * Contact Information Structure
 * Company contact details for footer display
 */
export interface ContactInfo {
  email: string;
  phone: string;
  address: string;
}

/**
 * Newsletter Configuration
 * Newsletter signup section configuration
 */
export interface NewsletterConfig {
  title: string;
  description: string;
  placeholder: string;
  buttonText: string;
}

/**
 * Notification Structure
 * User notifications for app header
 */
export interface Notification {
  id: number;
  title: string;
  message: string;
  time: string;
  unread: boolean;
}

/**
 * Mock Navigation API Response Structure
 * Simulates API response format for navigation data
 */
export interface NavigationApiResponse {
  status: 'success' | 'error';
  data: {
    landingNavigation: NavigationLink[];
    generalNavigation: GeneralNavLinkWithIcon[];
    appNavigation: NavigationLinkWithIcon[];
    footerLinks: FooterLinks;
    socialLinks: SocialLink[];
    contactInfo: ContactInfo;
    newsletter: NewsletterConfig;
    notifications: Notification[];
    lastUpdated: string;
  };
  message?: string;
}

/**
 * Mock Navigation Data
 * Centralized navigation configuration that mimics API response
 */
export const mockNavigationData: NavigationApiResponse = {
  status: 'success',
  data: {
    // Landing page header navigation
    landingNavigation: [
      { label: 'Features', href: '/#features' },
      { label: 'How It Works', href: '/#how-it-works' },
      { label: 'Pricing', href: '/#pricing' },
      { label: 'Why Tutara', href: '/#why-tutara' },
    ],

    // General header navigation with icons (used by Header component)
    generalNavigation: [
      { 
        label: 'Home', 
        path: '/', 
        icon: null as unknown as LucideIcon // Will be filled by component
      },
      { 
        label: 'Features', 
        path: '/#features', 
        icon: null as unknown as LucideIcon
      },
      { 
        label: 'How It Works', 
        path: '/#how-it-works', 
        icon: null as unknown as LucideIcon
      },
      { 
        label: 'Pricing', 
        path: '/#pricing', 
        icon: null as unknown as LucideIcon
      },
    ],

    // App sidebar navigation (using dynamic imports for icons)
    // Structure: Dashboard, My Learning section, My Development section, Progress section
    appNavigation: [
      // Dashboard (standalone at top)
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: null as unknown as LucideIcon, // Will be filled by component
        section: 'main'
      },

      // My Learning Section
      {
        label: 'My Learning',
        path: '/my-learning',
        icon: null as unknown as LucideIcon,
        section: 'learning'
      },
      {
        label: 'Explore',
        path: '/explore',
        icon: null as unknown as LucideIcon,
        section: 'learning'
      },
      {
        label: 'Study Plans',
        path: '/study-plans',
        icon: null as unknown as LucideIcon,
        section: 'learning'
      },
      {
        label: 'Concept Lab',
        path: '/concept-lab',
        icon: null as unknown as LucideIcon,
        section: 'learning'
      },

      // My Development Section
      {
        label: 'My Skills',
        path: '/skills',
        icon: null as unknown as LucideIcon,
        section: 'development'
      },
      {
        label: 'Career Roadmap',
        path: '/career-roadmap',
        icon: null as unknown as LucideIcon,
        section: 'development'
      },

      // Progress Section
      {
        label: 'Achievements',
        path: '/achievements',
        icon: null as unknown as LucideIcon,
        section: 'progress'
      },
      {
        label: 'Progress',
        path: '/progress',
        icon: null as unknown as LucideIcon,
        section: 'progress'
      },

      // Enterprise Features - Hidden for alpha (not ready for testers)
      // TODO: Re-enable when enterprise features are complete
      // {
      //   label: 'Programs',
      //   path: '/enterprise/programs',
      //   icon: null as unknown as LucideIcon,
      //   section: 'enterprise'
      // },
      // {
      //   label: 'Content Library',
      //   path: '/enterprise/content',
      //   icon: null as unknown as LucideIcon,
      //   section: 'enterprise'
      // },
      // {
      //   label: 'Employees',
      //   path: '/enterprise/employees',
      //   icon: null as unknown as LucideIcon,
      //   section: 'enterprise'
      // },
      // {
      //   label: 'Reports',
      //   path: '/enterprise/reports',
      //   icon: null as unknown as LucideIcon,
      //   section: 'enterprise'
      // },

      // Multi-Tenant Management - Hidden for alpha
      // TODO: Re-enable when multi-tenant features are complete
      // {
      //   label: 'Tenant Settings',
      //   path: '/tenant/settings',
      //   icon: null as unknown as LucideIcon,
      //   section: 'tenant'
      // },
      // {
      //   label: 'Domains',
      //   path: '/tenant/domains',
      //   icon: null as unknown as LucideIcon,
      //   section: 'tenant'
      // },
      // {
      //   label: 'Organizations',
      //   path: '/tenant/organizations',
      //   icon: null as unknown as LucideIcon,
      //   section: 'tenant'
      // },
    ],

    // Footer link categories — only links that resolve to real pages
    footerLinks: {
      product: [
        { label: 'Features', href: '/#features' },
        { label: 'How It Works', href: '/#how-it-works' },
        { label: 'Pricing', href: '/#pricing' },
        { label: 'Why Tutara', href: '/#why-tutara' },
      ],
      company: [
        { label: 'About Us', path: '/about' },
        { label: 'Contact', path: '/contact' },
      ],
      resources: [
        { label: 'Get Started', path: '/signup' },
        { label: 'Sign In', path: '/login' },
      ],
      legal: [
        { label: 'Privacy Policy', path: '/privacy' },
        { label: 'Terms of Service', path: '/terms' },
        { label: 'Cookie Policy', path: '/cookies' },
      ],
    },

    // Social media links
    socialLinks: [
      {
        label: 'Facebook',
        href: 'https://www.facebook.com/trigeardigital',
        icon: null as unknown as LucideIcon // Will be filled by component
      },
      {
        label: 'LinkedIn',
        href: 'https://www.linkedin.com/company/trigear-marketing/',
        icon: null as unknown as LucideIcon
      },
      {
        label: 'Instagram',
        href: 'https://www.instagram.com/trigeardigital',
        icon: null as unknown as LucideIcon
      },
    ],

    // Company contact information
    contactInfo: {
      email: 'info@trigear.in',
      phone: '+91 99000 03911',
      address: 'Trigear Digital Solutions Pvt Ltd, Office No 1904, 4th Floor, IS 19 Block, Urbtech Trade Centre, Sector - 132, Noida - 201304',
    },

    // Newsletter configuration
    newsletter: {
      title: 'Stay Updated with AI-Powered Learning',
      description: 'Get the latest updates on personalized learning strategies and AI innovations.',
      placeholder: 'Enter your email',
      buttonText: 'Subscribe',
    },

    // User notifications for app header
    notifications: [
      { 
        id: 1, 
        title: 'New course available', 
        message: 'Check out "Advanced React Patterns"', 
        time: '5m ago', 
        unread: true 
      },
      {
        id: 2,
        title: 'Keep learning!',
        message: 'Continue your learning journey today',
        time: '1h ago',
        unread: true
      },
      { 
        id: 3, 
        title: 'Course completed', 
        message: 'Congratulations on completing TypeScript Basics', 
        time: '2h ago', 
        unread: false 
      },
    ],

    lastUpdated: new Date().toISOString(),
  },
  message: 'Navigation data loaded successfully'
};

/**
 * API Mock Functions
 * These functions simulate API calls for navigation data
 */

/**
 * Simulate fetching landing page navigation
 */
export const fetchLandingNavigation = async (): Promise<NavigationLink[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 100));
  return mockNavigationData.data.landingNavigation;
};

/**
 * Simulate fetching general navigation
 */
export const fetchGeneralNavigation = async (): Promise<GeneralNavLinkWithIcon[]> => {
  await new Promise(resolve => setTimeout(resolve, 100));
  return mockNavigationData.data.generalNavigation;
};

/**
 * Simulate fetching app navigation
 */
export const fetchAppNavigation = async (): Promise<NavigationLinkWithIcon[]> => {
  await new Promise(resolve => setTimeout(resolve, 100));
  return mockNavigationData.data.appNavigation;
};

/**
 * Simulate fetching footer links
 */
export const fetchFooterLinks = async (): Promise<FooterLinks> => {
  await new Promise(resolve => setTimeout(resolve, 100));
  return mockNavigationData.data.footerLinks;
};

/**
 * Simulate fetching social links
 */
export const fetchSocialLinks = async (): Promise<SocialLink[]> => {
  await new Promise(resolve => setTimeout(resolve, 100));
  return mockNavigationData.data.socialLinks;
};

/**
 * Simulate fetching notifications
 */
export const fetchNotifications = async (): Promise<Notification[]> => {
  await new Promise(resolve => setTimeout(resolve, 100));
  return mockNavigationData.data.notifications;
};

/**
 * Simulate fetching all navigation data at once
 */
export const fetchAllNavigationData = async (): Promise<NavigationApiResponse> => {
  await new Promise(resolve => setTimeout(resolve, 150));
  return mockNavigationData;
};

/**
 * Direct access to navigation data (for synchronous usage)
 * Use these getters when you need immediate access without async operations
 */
export const getNavigationData = () => mockNavigationData.data;
export const getLandingNavigation = () => mockNavigationData.data.landingNavigation;
export const getGeneralNavigation = () => mockNavigationData.data.generalNavigation;
export const getAppNavigation = () => mockNavigationData.data.appNavigation;
export const getFooterLinks = () => mockNavigationData.data.footerLinks;
export const getSocialLinks = () => mockNavigationData.data.socialLinks;
export const getContactInfo = () => mockNavigationData.data.contactInfo;
export const getNewsletterConfig = () => mockNavigationData.data.newsletter;
export const getNotifications = () => mockNavigationData.data.notifications;